import _ from 'lodash';
import { api } from '@/services/api/main/axios';
import type {
  BillingCustomerState,
  BillingLimits,
  BillingPlan,
  BillingSubscription,
  BillingSubscriptionState,
} from '@/services/models';
import { availablePlanLimits } from '@/services/models';

const preparePlanLimits = <T extends { limits: unknown }>(payload: T): T =>
  ({
    ...payload,
    limits: {
      ..._.pick(payload.limits as Record<string, unknown>, availablePlanLimits),
    },
  }) as T;

export const getBillingPlansApiRequest = async (): Promise<BillingPlan[]> => {
  const res = await api.get<{ payload: BillingPlan[] }>('billing/plans');
  return res.data.payload.map(plan => preparePlanLimits(plan) as BillingPlan);
};

export const getBillingSubscriptionApiRequest = async (): Promise<BillingSubscriptionState> => {
  const res = await api.get<{ payload: BillingSubscription }>('billing/subscriptions');
  const payload = res.data.payload as BillingSubscription & {
    usage: Record<string, unknown>;
  };
  const preparedSubscription = {
    ...preparePlanLimits(payload),
    usage: {
      ..._.pick(payload.usage, availablePlanLimits),
    },
  };
  return preparedSubscription as BillingSubscriptionState;
};

export const getBillingCustomerApiRequest = async (): Promise<BillingCustomerState> => {
  const res = await api.get<{ payload: BillingCustomerState }>('billing/customer');
  return res.data.payload;
};

export const getBillingAddonsApiRequest = async (): Promise<unknown[]> => {
  const res = await api.get<{ payload: unknown[] }>('billing/addons');
  return res.data.payload;
};

export const putBillingAddonApiRequest = async (params: {
  id: keyof BillingLimits;
  quantity?: number | null;
  type?: 'estimates';
}): Promise<unknown> => {
  const url = params.type === 'estimates' ? 'billing/addon?type=estimates' : 'billing/addon';
  const body: Record<string, unknown> = { id: params.id };
  if (params.quantity !== null && params.quantity !== undefined) {
    body.quantity = params.quantity;
  }
  const res = await api.put<{ payload: unknown }>(url, body);
  return res.data.payload;
};

export const deleteBillingAddonApiRequest = async (params: {
  id: keyof BillingLimits;
  type?: 'estimates';
}): Promise<unknown> => {
  let url = `billing/addon/${params.id}`;
  if (params.type === 'estimates') {
    url = `${url}?type=estimates`;
  }
  const res = await api.delete<{ payload: unknown }>(url);
  return res.data.payload;
};

export const postUpdateBillingSubscriptionEstimatedApiRequest = async (
  payload: {
    planId: string;
  },
  ignoreInterceptor = false,
): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>(
    'billing/subscriptions?type=estimates',
    payload,
    {
      meta: { ignoreInterceptor },
    } as any,
  );
  return res.data.payload;
};

export const postUpdateBillingSubscriptionApiRequest = async (
  payload: {
    planId: string;
  },
  ignoreInterceptor = false,
): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/subscriptions', payload, {
    meta: { ignoreInterceptor },
  } as any);
  return res.data.payload;
};

export const getBillingInvoicesApiRequest = async (): Promise<unknown> => {
  const res = await api.get<{ payload: unknown }>('billing/invoices');
  return res.data.payload;
};

export const postUpdateBillingInfoApiRequest = async (payload: object): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/billing-info', payload);
  return res.data.payload;
};

export const postUpdateBillingCardApiRequest = async (
  payload: object,
  ignoreInterceptor = false,
): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/card', payload, {
    meta: { ignoreInterceptor },
  } as any);
  return res.data.payload;
};

export const postApplyBillingPromoApiRequest = async (payload: object): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/apply-promo', payload);
  return res.data.payload;
};

export const postBillingSetupIntentApiRequest = async (payload: {
  paymentMethodId: string;
}): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('/v1/billing/stripe/setup-intent', payload);
  return res.data.payload;
};

export const getBillingUnpaidInvoicesApiRequest = async (): Promise<unknown> => {
  const res = await api.get<{ payload: unknown }>('billing/invoices-unpaid');
  return res.data.payload;
};

export const postCollectBillingUnpaidInvoicesApiRequest = async (): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/collect-payment-invoices', {});
  return res.data.payload;
};

export const postStripePaymentIntentApiRequest = async (payload: unknown): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/stripe/payment-intent', payload);
  return res.data.payload;
};

export const postStripePaymentSourceApiRequest = async (payload: unknown): Promise<unknown> => {
  const res = await api.post<{ payload: unknown }>('billing/stripe/source', payload);
  return res.data.payload;
};
